import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, ChevronRight, Home, List as ListIcon, Star, Sun } from "@fg/icons";
import { useCreateTask, useTaskLists } from "@/api/hooks";
import { cn } from "@/lib/utils";
import { showToast } from "@/lib/toast";
import { isSmartList, todayKey, useTaskTimeZone, type ListKey } from "./taskModel";

/** Mail → Tasks: "Create task" from a message, and dropping messages on a task list. */

export const MESSAGE_DRAG_TYPE = "application/x-fg-message";

export interface MessageRef {
    id: string;
    subject: string;
    fromName?: string;
    fromEmail?: string;
}

export interface TaskTarget {
    key: ListKey;
    label: string;
    emoji?: string | null;
    color?: string | null;
}

/** My Day / Important / Tasks, then the user's lists — where a message can become a task. */
export function useTaskTargets(): TaskTarget[] {
    const lists = useTaskLists();
    return useMemo(() => [
        { key: "myday", label: "My Day" },
        { key: "important", label: "Important" },
        { key: "tasks", label: "Tasks" },
        ...(lists.data ?? []).map((l) => ({ key: l.id, label: l.name, emoji: l.emoji, color: l.color }))
    ], [lists.data]);
}

export function useCreateTaskFromMessage() {
    const create = useCreateTask();
    const tz = useTaskTimeZone();
    const navigate = useNavigate();
    return useCallback((messages: MessageRef[], key: ListKey) => {
        let made = 0;
        for (const m of messages) {
            const from = m.fromEmail ? `From: ${m.fromName ? `${m.fromName} <${m.fromEmail}>` : m.fromEmail}` : null;
            create.mutate(
                {
                    title: (m.subject || "(no subject)").slice(0, 512),
                    notes: from,
                    relatedMessageId: m.id,
                    listId: isSmartList(key) ? null : key,
                    isImportant: key === "important",
                    myDayDate: key === "myday" ? todayKey(tz) : null
                },
                {
                    onSuccess: (task) => {
                        made++;
                        if (made === messages.length) {
                            showToast(messages.length > 1 ? `${messages.length} tasks created` : "Task created", {
                                action: {
                                    label: "Open",
                                    onClick: () => navigate(`/tasks?list=${encodeURIComponent(key)}&task=${encodeURIComponent(task.id)}`)
                                }
                            });
                        }
                    },
                    onError: () => showToast("Couldn't create the task")
                }
            );
        }
    }, [create, tz, navigate]);
}

/** Reads the MessageList drag payload (single row or a checked batch). */
export function readMessageDrag(dt: DataTransfer): MessageRef[] | null {
    const raw = dt.getData(MESSAGE_DRAG_TYPE);
    if (!raw) return null;
    try {
        const p = JSON.parse(raw) as { id: string; ids?: string[]; items?: MessageRef[] };
        if (p.items && p.items.length > 0) return p.items;
        return (p.ids && p.ids.length > 0 ? p.ids : [p.id]).map((id) => ({ id, subject: "" }));
    } catch {
        return null;
    }
}

/**
 * "Tasks" section under the mail folders: drop a message (or a checked batch)
 * on a list to create tasks linked to them; click opens the list.
 */
export function TaskDropTargets() {
    const targets = useTaskTargets();
    const createFrom = useCreateTaskFromMessage();
    const navigate = useNavigate();
    const [open, setOpen] = useState(true);
    const [hover, setHover] = useState<string | null>(null);

    return (
        <section aria-label="Tasks" className="mt-1">
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                aria-expanded={open}
                className="flex w-full min-w-0 items-center gap-1.5 px-2 pb-1 pt-2.5 text-xs font-semibold text-[var(--mail-text-2)]"
            >
                {open ? <ChevronDown className="h-3.5 w-3.5 shrink-0" /> : <ChevronRight className="h-3.5 w-3.5 shrink-0" />}
                Tasks
                <span className="ml-auto text-[11px] font-normal text-[var(--mail-text-3)]">drop mail here</span>
            </button>
            {open ? targets.map((t) => {
                const Icon = t.key === "myday" ? Sun : t.key === "important" ? Star : t.key === "tasks" ? Home : ListIcon;
                return (
                    <button
                        key={t.key}
                        type="button"
                        onClick={() => navigate(`/tasks?list=${encodeURIComponent(t.key)}`)}
                        onDragOver={(e) => {
                            if (!e.dataTransfer.types.includes(MESSAGE_DRAG_TYPE)) return;
                            e.preventDefault();
                            e.dataTransfer.dropEffect = "copy";
                            if (hover !== t.key) setHover(t.key);
                        }}
                        onDragLeave={() => { if (hover === t.key) setHover(null); }}
                        onDrop={(e) => {
                            setHover(null);
                            const refs = readMessageDrag(e.dataTransfer);
                            if (!refs) return;
                            e.preventDefault();
                            createFrom(refs, t.key);
                        }}
                        className={cn(
                            "flex h-8 w-full min-w-0 items-center gap-2.5 rounded pl-[26px] pr-2 text-sm text-foreground transition-colors hover:bg-[var(--mail-hover)]",
                            "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary",
                            hover === t.key && "bg-[var(--mail-primary-soft)] ring-2 ring-primary"
                        )}
                        aria-label={`${t.label} — drop a message to create a task`}
                    >
                        {t.emoji
                            ? <span className="w-4 text-center text-sm leading-none" aria-hidden>{t.emoji}</span>
                            : <Icon className="h-4 w-4 shrink-0 text-foreground/70" style={{ color: t.color ?? undefined }} aria-hidden />}
                        <span className="truncate">{t.label}</span>
                    </button>
                );
            }) : null}
        </section>
    );
}
